import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import CreatableSelect from 'react-select/creatable'
import useSWR from 'swr'
import {
  fetchFeed,
  updateFeed,
  fetchFeedTags,
  setFeedTags,
  publishFeed,
  unpublishFeed,
  uploadImage,
  deleteImage,
  type FeedSummary,
} from '../services/api'
import { Badge, Banner, Button, Panel, ImageSlot } from '../components'
import pageStyles from '../styles/page.module.scss'
import formStyles from '../styles/forms.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import styles from './FeedDetail.module.scss'

interface SettingsForm {
  display_name: string
  is_gated: boolean
  hls_url: string
  dash_url: string
}

const FeedDetail: React.FC = () => {
  const { feedId } = useParams<{ feedId: string }>()

  const { data: feed, isLoading, error, mutate } = useSWR(
    feedId ? ['feed-detail', feedId] : null,
    () => fetchFeed(feedId!),
    { revalidateOnFocus: false }
  )

  const { data: tagsData, mutate: mutateTags } = useSWR(
    feedId ? ['feed-tags', feedId] : null,
    () => fetchFeedTags(feedId!),
    { revalidateOnFocus: false }
  )
  const selectedTags = tagsData?.tags || []

  const [form, setForm] = useState<SettingsForm>({ display_name: '', is_gated: false, hls_url: '', dash_url: '' })
  const [isSaving, setIsSaving] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [isUpdatingTags, setIsUpdatingTags] = useState(false)
  const [banner, setBanner] = useState<{ variant: 'success' | 'error'; message: string } | null>(null)
  const [tagError, setTagError] = useState<string | null>(null)

  const formFromFeed = (f: FeedSummary): SettingsForm => ({
    display_name: f.display_name || '',
    is_gated: f.is_gated,
    hls_url: f.hls_url || '',
    dash_url: f.dash_url || '',
  })

  const resetForm = useCallback(() => {
    if (feed) setForm(formFromFeed(feed))
  }, [feed])

  const seededFeedId = useRef<string | null>(null)
  useEffect(() => {
    if (feed && seededFeedId.current !== feed.feed_id) {
      seededFeedId.current = feed.feed_id
      setForm(formFromFeed(feed))
    }
  }, [feed])

  const hasChanges = (): boolean =>
    !!feed &&
    (form.display_name.trim() !== (feed.display_name || '') ||
      form.is_gated !== feed.is_gated ||
      form.hls_url.trim() !== (feed.hls_url || '') ||
      form.dash_url.trim() !== (feed.dash_url || ''))

  const feedTitle = feed ? feed.display_name || feed.name : 'Feed'

  const handleSaveSettings = async () => {
    if (!feedId) return
    setIsSaving(true)
    setBanner(null)
    try {
      await updateFeed(feedId, {
        display_name: form.display_name.trim() || null,
        is_gated: form.is_gated,
        hls_url: form.hls_url.trim() || null,
        dash_url: form.dash_url.trim() || null,
      })
      await mutate()
      setBanner({ variant: 'success', message: 'Channel settings saved.' })
    } catch (err: any) {
      setBanner({ variant: 'error', message: err.message || 'Failed to save settings.' })
    } finally {
      setIsSaving(false)
    }
  }

  const handlePublishToggle = async () => {
    if (!feedId || !feed) return
    setIsPublishing(true)
    setBanner(null)
    try {
      if (feed.published) {
        await unpublishFeed(feedId)
        setBanner({ variant: 'success', message: 'Feed unpublished — it no longer appears on rails.' })
      } else {
        await publishFeed(feedId)
        setBanner({ variant: 'success', message: 'Feed published — it can now appear on rails.' })
      }
      await mutate()
    } catch (err: any) {
      setBanner({ variant: 'error', message: err.message || 'Failed to update publish status.' })
    } finally {
      setIsPublishing(false)
    }
  }

  const handleUploadCard = async (file: File) => {
    if (!feedId) return
    await uploadImage('feed', feedId, file, { image_type: 'channel_card' })
    await mutate()
  }

  const handleRemoveCard = async () => {
    if (!feedId || !feed?.channel_card_image_id) return
    await deleteImage('feed', feedId, feed.channel_card_image_id)
    await mutate()
  }

  const handleSetTags = useCallback(async (tags: string[]) => {
    if (!feedId) return
    setIsUpdatingTags(true)
    setTagError(null)
    try {
      await setFeedTags(feedId, tags)
      await mutateTags()
    } catch (err: any) {
      setTagError(err.message || 'Failed to update tags.')
    } finally {
      setIsUpdatingTags(false)
    }
  }, [feedId, mutateTags])

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading feed</h2>
          <p>Failed to fetch feed details: {(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  if (isLoading || !feed) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading feed details...</h3>
          <p>Please wait while we fetch the feed.</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className={pageStyles.headerButtons}>
        <Button
          variant={feed.published ? 'secondary' : 'primary'}
          onClick={handlePublishToggle}
          disabled={isPublishing || (!feed.published && !feed.has_channel_card)}
          title={!feed.published && !feed.has_channel_card ? 'Add a channel card before publishing' : undefined}
        >
          {isPublishing ? 'Working...' : feed.published ? 'Unpublish' : 'Publish'}
        </Button>
      </div>

      <div className={pageStyles.pageHeader}>
        <h1>{feedTitle}</h1>
        <p>
          <Badge variant={feed.is_linear ? 'primary' : 'secondary'}>
            {feed.is_linear ? 'Linear' : 'Court'}
          </Badge>
          {' '}
          {feed.is_gated && <Badge variant="warning">Gated</Badge>}
          {' '}
          <Badge variant={feed.published ? 'success' : 'neutral'}>
            {feed.published ? 'Published' : 'Draft'}
          </Badge>
        </p>
      </div>

      {banner && (
        <Banner
          variant={banner.variant}
          message={banner.message}
          onDismiss={() => setBanner(null)}
        />
      )}

      <div className={styles.columns}>
        <div className={styles.column}>
          <Panel margin="none">
            <Panel.Header><h2>Details</h2></Panel.Header>
            <div className={formStyles.formField}>
              <div className={formStyles.fieldGroup}>
                <label>Feed key</label>
                <input type="text" value={feed.name} readOnly disabled />
                <p className={formStyles.hint}>
                  The feed key is fixed. Court routing is managed in Feed Mapping; stream manifests are set below.
                </p>
              </div>

              <div className={formStyles.fieldGroup}>
                <label>Display name</label>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, display_name: e.target.value }))}
                  placeholder={feed.name}
                  disabled={isSaving}
                />
                <p className={formStyles.hint}>Falls back to the feed key when left blank.</p>
              </div>

              <div className={formStyles.fieldGroup}>
                <label>Subscription</label>
                <label className={formStyles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.is_gated}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_gated: e.target.checked }))}
                    disabled={isSaving}
                  />
                  Gated — requires a subscription
                </label>
              </div>
            </div>

          </Panel>

          <Panel margin="none">
            <Panel.Header><h2>Streaming</h2></Panel.Header>
            <div className={formStyles.formField}>
              <div className={formStyles.fieldGroup}>
                <label>HLS manifest URL</label>
                <input
                  type="text"
                  value={form.hls_url}
                  onChange={(e) => setForm((prev) => ({ ...prev, hls_url: e.target.value }))}
                  placeholder="https://…/playlist.m3u8"
                  disabled={isSaving}
                />
              </div>

              <div className={formStyles.fieldGroup}>
                <label>DASH manifest URL</label>
                <input
                  type="text"
                  value={form.dash_url}
                  onChange={(e) => setForm((prev) => ({ ...prev, dash_url: e.target.value }))}
                  placeholder="https://…/manifest.mpd"
                  disabled={isSaving}
                />
                <p className={formStyles.hint}>
                  Set the playout manifests for this feed. Leave a field blank to fall back to the
                  Amagi URL derived from the feed key. DRM license endpoints are shared platform
                  config, not set per feed — a gated feed is DRM-protected automatically.
                </p>
              </div>
            </div>
          </Panel>

          {feed.is_linear && (
            <Panel margin="none">
              <Panel.Header><h2>Linear settings</h2></Panel.Header>
              <div className={formStyles.formField}>
                <div className={formStyles.fieldGroup}>
                  <label>EPG guide URL</label>
                  <input type="text" value={feed.epg_url || ''} readOnly disabled placeholder="—" />
                  <p className={formStyles.hint}>EPG configuration is managed by Amagi and is read-only here.</p>
                </div>
              </div>
            </Panel>
          )}

          {hasChanges() && (
            <div className={formStyles.formFooter}>
              <Button variant="secondary" onClick={resetForm} disabled={isSaving}>Cancel</Button>
              <Button onClick={handleSaveSettings} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </div>

        <div className={styles.column}>
          <Panel margin="none">
            <Panel.Header><h2>Image</h2></Panel.Header>
            <ImageSlot
              image={feed.channel_card_url ? { url: feed.channel_card_url } : null}
              aspectRatio="16 / 9"
              hint="16:9 image shown when this feed appears on an app rail. Required before publishing."
              onUpload={handleUploadCard}
              onRemove={feed.channel_card_image_id ? handleRemoveCard : undefined}
              disabled={isPublishing}
            />
          </Panel>

          <Panel margin="none">
            <Panel.Header><h2>Tags</h2></Panel.Header>
            <div className={formStyles.fieldGroup}>
              <label>Merchandising tags</label>
              {tagError && <div className={formStyles.errorText}>{tagError}</div>}
              <CreatableSelect
                isMulti
                value={selectedTags.map((tag) => ({ value: tag, label: tag }))}
                onChange={(options) => handleSetTags(options ? options.map((opt) => opt.value) : [])}
                onCreateOption={(input) => handleSetTags([...selectedTags, input])}
                isDisabled={isUpdatingTags}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
                placeholder="Type to add a tag..."
                formatCreateLabel={(input) => `Add tag "${input}"`}
                noOptionsMessage={() => 'Type to add a tag'}
              />
              <p className={formStyles.hint}>Tags select which rails this feed appears on.</p>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  )
}

export default FeedDetail
